


import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Lenis from "@studio-freight/lenis";

gsap.registerPlugin(ScrollTrigger);


const MOBILE_MAX_VW = 991;           
const ANTICIPATE_PIN = 2;
const LENIS_LERP = 0.065;
const LENIS_WHEEL_MULTIPLIER = 1.25;
const LENIS_TICKER_KEY = "__lenisTickerBridge";


let lenis = null;
let wired = false;


const isIOS = () =>
  /iP(ad|hone|od)/.test(navigator.platform) ||
  (navigator.userAgent.includes("Mac") && "ontouchend" in document);

const prefersReduced = () =>
  globalThis.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;

function setupVHVars() {
  const apply = () => {
    const baseH = window.innerHeight;
    const vvH = window.visualViewport ? visualViewport.height : null;
    
    const h = vvH && vvH >= baseH * 0.85 ? vvH : baseH;
    document.documentElement.style.setProperty("--vh", `${h}px`);
  };
  let t;
  const debounced = () => { clearTimeout(t); t = setTimeout(apply, 120); };
  apply();
  window.addEventListener("resize", debounced, { passive: true });
  if (window.visualViewport) visualViewport.addEventListener("resize", debounced, { passive: true });
}

function imagesReady(cb) {
  if (document.readyState === "complete") return cb();
  window.addEventListener("load", cb, { once: true });
}

function shouldUseSmooth() {
  const w = window.innerWidth || document.documentElement.clientWidth || 0;
  if (w <= MOBILE_MAX_VW) return false;        
  if (prefersReduced()) return false;
  return true;
}


function configureScrollTrigger() {
  if (ScrollTrigger.config) {
    ScrollTrigger.config({ ignoreMobileResize: true });
  }
  if (ScrollTrigger.normalizeScroll) {
    try { ScrollTrigger.normalizeScroll(false); } catch {}
  }
}


function wireLenisWithScrollTrigger() {
  const smooth = shouldUseSmooth();

  if (smooth) {
    
    lenis = new Lenis({
      smoothWheel: true,
      smoothTouch: false,      
      normalizeWheel: false,
      wheelMultiplier: LENIS_WHEEL_MULTIPLIER,
      gestureOrientation: "vertical",
      syncTouch: true,
      lerp: LENIS_LERP
    });
    globalThis.lenis = lenis;

    const previousTicker = globalThis[LENIS_TICKER_KEY];
    if (previousTicker) {
      try { gsap.ticker.remove(previousTicker); } catch {}
    }
    const tickerBridge = (time) => {
      lenis.raf(time * 1000);
    };
    globalThis[LENIS_TICKER_KEY] = tickerBridge;
    gsap.ticker.add(tickerBridge);

    
    gsap.ticker.lagSmoothing(0);
    ScrollTrigger.scrollerProxy(document.body, {
      scrollTop(value) {
        if (arguments.length) {
          lenis.scrollTo(value, { immediate: true });
        }
        return window.pageYOffset || document.documentElement.scrollTop || 0;
      },
      getBoundingClientRect() {
        return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
      },
      pinType: isIOS() ? "transform" : "fixed"
    });

    lenis.on("scroll", () => ScrollTrigger.update());
    ScrollTrigger.addEventListener("refresh", () => {
      if (typeof lenis.resize === "function") {
        lenis.resize();
      } else if (typeof lenis.update === "function") {
        lenis.update();
      }
    });
    console.info("[scroll-foundation] Smooth ON (Lenis), pinType:", isIOS() ? "transform" : "fixed");
  } else {
    
    lenis = null;
    globalThis.lenis = null;
    
    try { ScrollTrigger.scrollerProxy(document.body, {}); } catch {}
    console.info("[scroll-foundation] Smooth OFF (<= " + MOBILE_MAX_VW + "px oder prefers-reduced)");
  }
}


function setupStackPins() {
  const stacks = document.querySelectorAll("[data-pin-stack], .stack-pin, .pin-stage");
  if (!stacks.length) return;

  stacks.forEach((section, idx) => {
    const viewportH = window.innerHeight;
    const contentH  = section.scrollHeight;
    const minEnd    = Math.max(viewportH, contentH);

    ScrollTrigger.create({
      id: `stack-${idx}`,
      trigger: section,
      start: "top top",
      end: `+=${minEnd}`,
      pin: true,
      pinSpacing: true,
      anticipatePin: ANTICIPATE_PIN,
      invalidateOnRefresh: true,
      onRefresh: self => {
        const vH = window.innerHeight;
        const newEnd = Math.max(vH, section.scrollHeight);
        self.end = self.start + newEnd;
      }
      
    });
  });

  imagesReady(() => {
    ScrollTrigger.refresh(true);
  });
}


export function initScrollFoundation() {
  if (wired) return; 
  wired = true;

  setupVHVars();
  const smooth = shouldUseSmooth();
  configureScrollTrigger();
  
  
  if (!smooth) {
    console.log("📱 Mobile/Tablet detected (≤" + MOBILE_MAX_VW + "px or prefers-reduced) - Lenis disabled");
    window.lenisDisabled = true;
    lenis = null;
    globalThis.lenis = null;
    
    
    setupStackPins();
    
    imagesReady(() => {
      ScrollTrigger.refresh(true);
    });
    
    
    let lastModeSmooth = false;
    window.addEventListener("resize", () => {
      const now = shouldUseSmooth();
      if (now !== lastModeSmooth) {
        console.warn("[scroll-foundation] viewport crossed smooth breakpoint — reload recommended");
        lastModeSmooth = now;
      }
    }, { passive: true });
    
    console.log("[scroll-foundation] Running without Lenis (native scroll)");
    return null;
  }
  
  
  wireLenisWithScrollTrigger();
  setupStackPins();

  imagesReady(() => {
    ScrollTrigger.refresh(true);
  });

  
  let lastModeSmooth = smooth;
  window.addEventListener("resize", () => {
    const now = shouldUseSmooth();
    if (now !== lastModeSmooth) {
      console.warn("[scroll-foundation] viewport crossed smooth breakpoint — reload recommended");
      lastModeSmooth = now;
    }
  }, { passive: true });

  console.log("[scroll-foundation] wired with Lenis");
}

export function getLenis() {
  return lenis;
}
